import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useFormState } from 'react-final-form';
import ListingPrices from 'components/ListingPrices';
import { ListingPriceRow } from 'components/ListingPrices/ListingPrices.component';
import { Listing, RequiredVisibleInfo } from 'models/Listing';
import utils from 'utils';
import { REQUIREMENT_PRICE_ROW } from 'router/subrouters/ListingCreationWizard/data/publicListingRequirements';
import PreviewMissingFieldsContext from '../../providers/PreviewMissingFields/PreviewMissingFields.context';

/**
 * Urgent listings have their own Statement of Information holding only revenue,
 * EBITDA and averageStockValue - no price guide and no stock-at-value selector.
 */
const GOING_CONCERN_ROWS: ListingPriceRow[] = [
  'askingPrice',
  'revenue',
  'EBITDA',
  'averageStockValue',
];

const URGENT_ROWS: ListingPriceRow[] = [
  'revenue',
  'EBITDA',
  'averageStockValue',
];

type ListingPricesPreviewProps = {
  className?: string;
  isUrgent?: boolean;
  amountRequiredVisibleInfo?: RequiredVisibleInfo;
  amountFromRequiredVisibleInfo?: RequiredVisibleInfo;
  amountToRequiredVisibleInfo?: RequiredVisibleInfo;
  revenueRequiredVisibleInfo?: RequiredVisibleInfo;
  EBITDARequiredVisibleInfo?: RequiredVisibleInfo;
  averageStockValueRequiredVisibleInfo?: RequiredVisibleInfo;
};

/**
 * Display half of the preview-mode financial block. Reads from form state
 * rather than the listing so an edit shows without a server round trip, and
 * renders every row - placeholdered when empty - so each stays clickable.
 */
const ListingPricesPreview: React.FC<ListingPricesPreviewProps> = (props) => {
  const {
    className,
    isUrgent = false,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = props;

  const classes = classNames('listing-prices--preview', className);

  const { values } = useFormState<Partial<Listing>>({
    subscription: { values: true },
  });

  const { isMarked } = useContext(PreviewMissingFieldsContext);

  /**
   * The block opens as one editor, but each metric is its own requirement, so
   * the mark lands on the tile that is actually outstanding.
   */
  const missingRows = useMemo(
    () =>
      (
        Object.keys(REQUIREMENT_PRICE_ROW) as Array<
          keyof typeof REQUIREMENT_PRICE_ROW
        >
      )
        .filter((key) => isMarked?.(key))
        .map<ListingPriceRow>((key) => REQUIREMENT_PRICE_ROW[key]),
    [isMarked],
  );

  /**
   * Form state holds money as dotted strings. Empty must stay undefined rather
   * than become 0, which mapValueToMoneyFormat renders as "< $50K".
   */
  const toNumber = useCallback((value: string | number) => {
    if (value === null || value === undefined || value === '') return undefined;

    return typeof value === 'number'
      ? value
      : utils.getIntegerFromDottedNumber(value);
  }, []);

  return (
    <ListingPrices
      className={classes}
      isPreview
      alwaysShowRows={isUrgent ? URGENT_ROWS : GOING_CONCERN_ROWS}
      missingRows={missingRows}
      askingPrice={isUrgent ? undefined : values.askingPriceSelect}
      amount={toNumber(values.amount)}
      amountFrom={toNumber(values.amountFrom)}
      amountTo={toNumber(values.amountTo)}
      revenue={toNumber(values.revenue)}
      EBITDA={toNumber(values.EBITDA)}
      averageStockValue={toNumber(values.averageStockValue)}
      stockAtValue={values.stockAtValue}
      currency={values.currency}
      amountRequiredVisibleInfo={amountRequiredVisibleInfo}
      amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
      amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
      revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
      EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
      averageStockValueRequiredVisibleInfo={
        averageStockValueRequiredVisibleInfo
      }
    />
  );
};

export default ListingPricesPreview;
